/**
 * The first position of a document (#2308).
 *
 * A document always needs a position, so while an editable one has none, the
 * blank row "Zeile hinzufügen" would add stands there already — on a new
 * document from the moment it opens. It is filled like any blank row: by
 * searching for the article in the row.
 *
 * A blank row is no position: it is not saved, it is no unsaved input and it
 * does not hold up a save. Where a document needs at least one position, it
 * does not count.
 */
export interface PaloPositionLike {
    product?: unknown;
    name?: Partial<Record<string, string | null>> | null;
}

/** The row still waits for its article — the same test as `isBlank` in the position tables. */
export const isBlankPosition = (item: PaloPositionLike): boolean =>
    !item.product && 0 === Object.values(item.name ?? {}).filter(Boolean).length;

/**
 * The rows without the blank ones. `isArticleRow` narrows it to article rows
 * where a document also carries surcharges or text rows, which keep their name
 * elsewhere.
 */
export const withoutBlankPositions = <T>(items: T[], isArticleRow: (item: T) => boolean = () => true): T[] =>
    items.filter(item => !(isArticleRow(item) && isBlankPosition(item as unknown as PaloPositionLike)));

/**
 * The rows a document shows: its own, or a blank one while it is editable and
 * has none. `blank` must look the same on every call: the unsaved marker
 * compares the form against the loaded document, and both pass through here.
 */
export const withFirstPosition = <T>(items: T[], editable: boolean, blank: () => T): T[] =>
    editable && 0 === items.length ? [blank()] : items;
